/**
 * @author Petr Mikušek <petr.mikusek@ki-wi.cz>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

// @ts-check

/* eslint-env browser */

import AssertionError from 'assertion-error';

/**
 * @import { IDateUTC, IDateUTCConstructor, NowFactory } from '.'
 */

/**
 * Minimální timestamp pro Date.
 * @see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date
 */
export const minTime = -8640000000000000;

/**
 * Maximální timestamp pro Date.
 * @see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date
 */
export const maxTime = 8640000000000000;

/**
 * Továrna na "now" pomocí {@link IDateUTCConstructor}.
 * @implements {NowFactory}
 */
export class DateNowFactory {
  /**
   * @param {IDateUTCConstructor} [DateConstructor]
   */
  constructor(DateConstructor = Date) {
    this.Date = DateConstructor;
  }

  /** @type {NowFactory['now']} */
  now() {
    return new this.Date();
  }
}

/**
 * Továrna na "now" pomocí pevné {@link IDateUTC} hodnoty.
 * @implements {NowFactory}
 */
export class FixedDateNowFactory {
  /**
   * @param {IDateUTC} date
   */
  constructor(date) {
    this.date = date;
  }

  /** @type {NowFactory['now']} */
  now() {
    return this.date;
  }
}

// TODO: "Integračně" testováno v rámci testů AnalyticsTracker. Nepřesunout tedy
// nějaké testy zde lokálně?
/**
 * Zjistí začátek nebo otevřený konec stávající lokální hodiny.
 * @param {DateConstructor} Date Pro kopii a práci s lokálním časem a TZ
 * (explicitně pro testovatelnost).
 * @param {IDateUTC} now
 * @param {boolean} next Hodnota `false` hledá začátek stávající hodiny,
 * `true` pro začátek následující (tj. otevřený konec stávající).
 * @returns {IDateUTC} Vždy nová instance Date, může vrátit hodnotu NaN,
 * pokud je vstup NaN nebo se při výpočtu dostaneme mimo rozsah Date.
 * @throws {TypeError} Pokud by z nějakého důvodu výsledek nebyl NaN a neplatilo
 * {@link isWholeLocalHour}, což by byl zcela zásadní bug v algoritmu!
 */
export function wholeLocalHour(Date, now, next) {
  const date = new Date(now.getTime());
  const nextOneZero = Number(next);

  function postcondition() {
    const dateTime = date.getTime();
    const nowTime = now.getTime();
    if (
      !isNaN(dateTime) &&
      !isNaN(nowTime) &&
      ((!next && nowTime < dateTime) || (next && nowTime >= dateTime))
    ) {
      throw new AssertionError('wholeLocalHour postcondition', {
        date,
        now,
        next,
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
      });
    }
  }

  // Pokusíme se vynulovat minuty (resp. pro next najít následující hodinu 60m),
  // sekundy a milisekundy v lokálním čase. Kvůli možným přechodům přes případnou
  // hranici DST vlevo nebo vpravo nemusí bohužel v JS uspět - myšleno tak,
  // že po operaci get jednotlivých komponent nevrátí nulu!
  date.setMinutes(60 * nextOneZero, 0, 0);
  if (isNaN(date.getTime()) || isWholeLocalHour(date)) {
    postcondition();
    return date;
  }

  // Druhý pokus, kde pro hledání doleva musíme hledat předchozí hodinu (-60m)
  // a pro hledání doprava analogicky následující hodinu (60m), protože
  // setMinutes(0, 0, 0) na pomyslně zarovnané hodině může vrátit stejné Date!
  date.setMinutes(120 * nextOneZero - 60, 0, 0);
  if (isNaN(date.getTime()) || isWholeLocalHour(date)) {
    postcondition();
    return date;
  }

  // TODO: Může se i na druhý pokus stát, že by m:s:ms nebyly nuly? Asi by nemělo,
  // pokud jsou změny DST nebo TZ offsetu dostatečně daleko od sebe aby posun
  // doufejme max. o jednu hodinu nenarazil na další změnu offsetu...
  throw new AssertionError(
    'date is not a whole local hour after calling wholeLocalHour',
    {
      date,
      now,
      next,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
    }
  );
}

/**
 * Zjistí, zda zadané datum je celká lokální hodina, tj. má pro minuty, sekundy
 * i milisekundy hodnoty 0.
 *
 * Pozor, že po volání {@link Date.prototype.setMinutes}(0, 0, 0) nemusí platit
 * {@link isWholeLocalHour}, viz nutné vícefázové ošetření v {@link wholeLocalHour}!
 * @param {Date} d
 * @returns {boolean}
 */
export function isWholeLocalHour(d) {
  // Díky explicitnímu testu === 0 je ošetřeno i NaN Date, jelikož get jakékoliv
  // komponenty také vrátí NaN, takže není potřeba explicitně testovat předem.
  return (
    d.getMinutes() === 0 && d.getSeconds() === 0 && d.getMilliseconds() === 0
  );
}
